import { currentLocale } from '@trello/locale';
import { isSafari, isMac } from '@trello/browser';

const numberFormatter = new Intl.NumberFormat(currentLocale);
let compactNumberFormatter: Intl.NumberFormat | undefined;
const percentageFormatter = new Intl.NumberFormat(currentLocale, {
  style: 'percent',
});
const twoDecimalFormatter = new Intl.NumberFormat(currentLocale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const taxRateFormatter = new Intl.NumberFormat(currentLocale, {
  style: 'percent',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

export const asNumber = (val: number) => numberFormatter.format(val);
export const asCompactNumber = (val: number) => {
  if (compactNumberFormatter) {
    return compactNumberFormatter.format(val);
  } else {
    // In Safari 14.1 on MacOS Mojave, using Intl.NumberFormat with "notation: 'compact'" results in an exception that
    // crashes Trello - https://ops.internal.atlassian.com/jira/browse/HOT-95177. As a workaround, we fall back to
    // 'standard' notation instead. This means that on the public templates gallery users of Safari 14.1 will see
    // "15,400 Copies, 70,400 Views" instead of "15.4K Copies, 70.5K Views". This solution should only be temporary, and
    // we should aim to find a solution or polyfill that allows us to use 'compact' numbers in Safari 14.1.
    const notation = !(
      isMac() &&
      isSafari() &&
      navigator.userAgent.includes('Version/14.1')
    )
      ? 'compact'
      : 'standard';
    compactNumberFormatter = new Intl.NumberFormat(currentLocale, {
      notation,
      compactDisplay: 'short',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
    return compactNumberFormatter.format(val);
  }
};

export const asPercentage = (val: number) => percentageFormatter.format(val);
export const asMoney = (val: number, normalRounding: boolean = false) => {
  if (normalRounding) {
    return twoDecimalFormatter.format(val);
  } else {
    // Trello displays prices like 9.999 as $9.99
    return twoDecimalFormatter.format(Math.floor(val * 100) / 100);
  }
};
export const asTaxRate = (val: number) => taxRateFormatter.format(val);
